import { useState, useEffect, useCallback, useMemo } from 'react';
import { getCompleteHierarchy, HierarchyNode, GetHierarchyParams, CompleteHierarchyResponse } from 'core/API/hierarchy';

interface UseHierarchyReturn {
	hierarchyData: CompleteHierarchyResponse | null;
	loading: boolean;
	error: string | null;
	refetch: () => void;
	buildHierarchyTree: () => HierarchyTreeNode[];
}

export interface HierarchyTreeNode extends HierarchyNode {
	children: HierarchyTreeNode[];
	parent?: HierarchyTreeNode;
}

/**
 * Fetches the complete organisational hierarchy and provides a helper to
 * transform the flat list into a nested tree structure.
 *
 * - Handles loading/error state internally.
 * - Exposes a `refetch` callback for manual refresh.
 * - `buildHierarchyTree` constructs a parent–child tree from the flat
 *   `combinedHierarchy` array, with circular-reference protection.
 *
 * @param params - Optional pagination and focus parameters for the hierarchy API.
 * @returns An object containing `hierarchyData`, `loading`, `error`, `refetch`,
 *   and `buildHierarchyTree`.
 *
 * @example
 * const { hierarchyData, buildHierarchyTree, loading } = useHierarchy();
 * const tree = buildHierarchyTree();
 */
export const useHierarchy = (params: GetHierarchyParams = {}): UseHierarchyReturn => {
	const [hierarchyData, setHierarchyData] = useState<CompleteHierarchyResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Memoize params to prevent infinite loops
	const memoizedParams = useMemo(() => ({
		page: params.page || 1,
		itemsPerPage: params.itemsPerPage || 50,
		inFocus: params.inFocus || null,
	}), [params.page, params.itemsPerPage, params.inFocus]);

	const fetchHierarchy = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
      const response = await getCompleteHierarchy(memoizedParams);
      // console.log('Fetched hierarchy data:', response);
			setHierarchyData(response);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch hierarchy');
		} finally {
			setLoading(false);
		}
	}, [memoizedParams]);

	const buildHierarchyTree = useCallback((): HierarchyTreeNode[] => {
		if (!hierarchyData || hierarchyData.combinedHierarchy.length === 0) return [];

		const nodes = hierarchyData.combinedHierarchy;
		// console.log("building tree from nodes", nodes);
		// console.log('Building tree from nodes:', nodes);
		const nodeMap = new Map<number, HierarchyTreeNode>();
		const rootNodes: HierarchyTreeNode[] = [];

		// Create a map of all nodes with children array
		nodes.forEach(node => {
			nodeMap.set(node.id, {
				...node,
				children: []
			});
    });
    // console.log("node map", nodeMap)

		// console.log('Node map created:', Array.from(nodeMap.entries()));

		// Build the tree structure with circular reference protection
		const processedNodes = new Set<number>();

		nodes.forEach(node => {
			const currentNode = nodeMap.get(node.id);
			if (!currentNode || processedNodes.has(node.id)) return;

			// Prevent circular references
			let parentId = node.parentId;
			const visited = new Set<number>();
			
			// Check for circular references by walking up the parent chain
			while (parentId !== null && nodeMap.has(parentId)) {
				if (visited.has(parentId)) {
					// Circular reference detected, make this a root node
					parentId = null;
					break;
				}
				visited.add(parentId);
				const parent = nodes.find(n => n.id === parentId);
				parentId = parent ? parent.parentId : null;
			}

			if (node.parentId === null || !nodeMap.has(node.parentId)) {
				// Root node (organization should be at root)
				// console.log('Adding root node:', node.name, node.type);
				rootNodes.push(currentNode);
			} else {
				// Child node
				const parentNode = nodeMap.get(node.parentId);
				if (parentNode) {
					// console.log('Adding child node:', node.name, node.type, 'to parent:', parentNode.name);
					currentNode.parent = parentNode;
					parentNode.children.push(currentNode);
				}
			}
			
			processedNodes.add(node.id);
		});

		// console.log('Root nodes before sorting:', rootNodes.map(n => `${n.name} (${n.type})`));

		// Sort by type priority: organization > custom > workplace
		const typePriority = { organization: 0, group: 1, workplace: 2 };
		
		const sortTree = (tree: HierarchyTreeNode[]): HierarchyTreeNode[] => {
			return tree
				.sort((a, b) => {
					const aPriority = typePriority[a.type] ?? 3;
					const bPriority = typePriority[b.type] ?? 3;
					if (aPriority !== bPriority) return aPriority - bPriority;
					return a.name.localeCompare(b.name);
				})
				.map(node => ({
					...node,
					children: sortTree(node.children)
				}));
		};

		const sortedTree = sortTree(rootNodes);
		// console.log('Final sorted tree:', sortedTree);
		return sortedTree;
	}, [hierarchyData]);

	const refetch = useCallback(() => {
		fetchHierarchy();
	}, [fetchHierarchy]);

	useEffect(() => {
		fetchHierarchy();
	}, [fetchHierarchy]);

	return {
		hierarchyData,
		loading,
		error,
		refetch,
		buildHierarchyTree,
	};
};
